import React from "react";
import {
  Modal,
  ModalHeader,
  ModalProps,
} from "@sonder/ui/source/styled/components/Modal";
import { ModalFooter } from "@sonder/ui/source/styled/fragments/Modal";
import { PrimaryButton } from "@sonder/ui/source/styled/fragments/PrimaryButton";
import { DangerousValidationException } from "@sonder/ui/source/styled/fragments/ValidationException";
import { useSegment } from "analytics/segment";
import { useStore as useAuthStore } from "auth/store";
import { useErrorReporterFiltered } from "core/hooks/useErrorReporterFiltered";
import { useGetTaskScheduleIdsQuery } from "core/queries/GetTaskScheduleIds";
import { useScheduleTasks } from "core/queries/ScheduleTasks";
import { useStore } from "schedule/store";
import { useStore as useTasksStore } from "tasks/store";
import { TypeEnum } from "typings/globalGQLTypes";

interface AutoScheduleModalType
  extends Pick<ModalProps, "isOpen" | "onClose"> {}

export const AutoScheduleModal = ({
  isOpen,
  onClose,
}: AutoScheduleModalType) => {
  const errorReporter = useErrorReporterFiltered();
  const { track } = useSegment();
  const { state: authState } = useAuthStore();
  const {
    state: {
      filters: { assignee, buildingIds },
    },
  } = useTasksStore();
  const cityId = authState?.user?.cityId;
  const {
    state: {
      dateRange,
      filters: {
        cleaningCompanyIds,
        cleaningRequestCategory,
        sameDayCleansOnly,
        scheduleStatus,
        status,
      },
    },
  } = useStore();
  const endAt = dateRange.endAt.toISOString();
  const startAt = dateRange.startAt.toISOString();
  const taskFilter = {
    assignee,
    dueOrLatestEndAt: endAt,
    scheduledOrEarliestStartAt: startAt,
    status,
  };

  const variables = {
    cursor: "0",
    limit: 1000,
    filter: {
      ...taskFilter,
      buildingIds: buildingIds || [],
      city: cityId as string,
      cleaningCompanyIds,
      cleaningRequestCategory,
      sameDayCleansOnly,
      scheduleStatus,
      taskType: [TypeEnum.Tom_Tasks_Clean, TypeEnum.Tom_Tasks_SpaceClean],
    },
  };

  const { data, loading, error } = useGetTaskScheduleIdsQuery({
    skip: !cityId || !isOpen,
    variables,
  });

  const [onScheduleTask, { error: scheduleError, loading: isScheduling }] =
    useScheduleTasks({
      refetchQueries: ["GetTaskSchedules", "GetSchedulerMetrics"],
      awaitRefetchQueries: true,
    });

  const taskIds = (data?.taskSchedulerConnections?.places || []).reduce(
    (acc: string[], memo) => {
      return [...acc, ...(memo.relatedTasks || []).map(task => task.id)];
    },
    [],
  );

  const isSubmitDisabled =
    loading || !!error || !taskIds.length || isScheduling;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      {({ closeModal }) => {
        const onHandleClick = async () => {
          try {
            await onScheduleTask({
              variables: {
                cityId: cityId || "",
                endAt,
                startAt,
                taskIds,
              },
            });
            track({
              eventName: "schedule_view",
              action: "click",
              element: "auto_schedule_modal_submit",
              props: {},
            });
            closeModal();
          } catch (e) {
            errorReporter.notify(e);
          }
        };
        return (
          <>
            <ModalHeader
              title={loading ? "Loading..." : "Preschedule"}
              onClose={closeModal}
            />
            {!loading && (
              <>
                {taskIds.length !== 0 && (
                  <p>{taskIds.length} cleans(s) will be Prescheduled.</p>
                )}
                {assignee?.length !== 0 && (
                  <p>{assignee?.length || 0} assignee(s) are selected.</p>
                )}
                {buildingIds?.length !== 0 && (
                  <p>{buildingIds?.length || 0} building(s) are selected.</p>
                )}
              </>
            )}
            {scheduleError?.message && (
              <DangerousValidationException message={scheduleError.message} />
            )}
            <ModalFooter>
              <PrimaryButton
                disabled={isSubmitDisabled}
                onClick={onHandleClick}
              >
                Preschedule
              </PrimaryButton>
            </ModalFooter>
          </>
        );
      }}
    </Modal>
  );
};
